import { zodResolver } from '@hookform/resolvers/zod';
import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { FormProvider, useFieldArray, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { AiOutlinePlus } from 'react-icons/ai';
import { twJoin } from 'tailwind-merge';
import {
  Maybe,
  RouteTypeOfLineEnum,
  SubstituteOperatingPeriodSettingsInfoFragment,
} from '../../../../generated/graphql';
import {
  setIsOccasionalSubstitutePeriodFormDirtyAction,
  useAppDispatch,
} from '../../../../redux';
import { SubstituteDayOfWeek } from '../../../../types/enums';
import { AllOptionEnum } from '../../../../utils';
import {
  findEarliestTime,
  findLatestTime,
  mapDurationToShortTime,
  mapToISODate,
  today,
} from '../../../../utils/time';
import { SimpleButton, TextAndIconButton } from '../../../common/Buttons';
import { submitFormByRef } from '../../../common/Forms';
import { useDirtyFormBlockNavigation } from '../../../common/Forms/NavigationBlocker';
import { Row, Visible } from '../../../common/LayoutComponents';
import { ConfirmationDialog } from '../../../common/Modals';
import {
  mapDateTimeToFormState,
  mapLineTypes,
  parseSubstituteDayOfWeek,
} from '../utils';
import {
  FormState,
  PeriodType,
  schema,
} from './OccasionalSubstitutePeriodForm.types';
import { OccasionalSubstitutePeriodRow } from './OccasionalSubstitutePeriodRow';

const testIds = {
  cancelButton: 'OccasionalSubstitutePeriodForm::cancelButton',
  saveButton: 'OccasionalSubstitutePeriodForm::saveButton',
  addRowButton: 'OccasionalSubstitutePeriodForm::addRowButton',
};

function generateLineTypes(): Array<RouteTypeOfLineEnum | AllOptionEnum> {
  return [...Object.values(RouteTypeOfLineEnum), AllOptionEnum.All];
}

const emptyRowObject: PeriodType = {
  periodName: '',
  lineTypes: generateLineTypes(),
  substituteDayOfWeek: SubstituteDayOfWeek.NoTraffic,
  beginDate: mapToISODate(today()) ?? '',
  endDate: mapToISODate(today()) ?? '',
  beginTime: '04:30',
  endTime: '28:30',
  toBeDeleted: false,
  isPreset: false,
};

function mapDurationToString(duration: Maybe<Temporal.Duration> | undefined) {
  if (duration) {
    return mapDurationToShortTime(duration);
  }

  return '';
}

function convertToPeriodSchema(
  periods: ReadonlyArray<SubstituteOperatingPeriodSettingsInfoFragment>,
): FormState {
  return {
    periods: periods.map((period) => {
      const supersededDates = period.substitute_operating_day_by_line_types.map(
        (day) => day.superseded_date,
      );

      const periodBeginDate = supersededDates.length
        ? findEarliestTime(supersededDates)
        : undefined;

      const periodEndDate = supersededDates.length
        ? findLatestTime(supersededDates)
        : undefined;

      return {
        periodId: period.substitute_operating_period_id,
        periodName: period.period_name,
        beginDate: mapDateTimeToFormState(periodBeginDate),
        endDate: mapDateTimeToFormState(periodEndDate),
        beginTime: mapDurationToString(
          period.substitute_operating_day_by_line_types[0].begin_time,
        ),
        endTime: mapDurationToString(
          period.substitute_operating_day_by_line_types[0].end_time,
        ),
        substituteDayOfWeek: parseSubstituteDayOfWeek(
          period.substitute_operating_day_by_line_types[0]
            .substitute_day_of_week,
        ),
        lineTypes: mapLineTypes(period),
        toBeDeleted: false,
        isPreset: false,
      };
    }),
  };
}

type OccasionalSubstitutePeriodFormProps = {
  readonly onSubmit: (state: FormState) => void;
  readonly periods: ReadonlyArray<SubstituteOperatingPeriodSettingsInfoFragment>;
  readonly loading: boolean;
};

export const OccasionalSubstitutePeriodForm: FC<
  OccasionalSubstitutePeriodFormProps
> = ({ onSubmit, periods, loading }) => {
  const { t } = useTranslation();
  const [isResetting, setIsResetting] = useState<boolean>(false);
  const dispatch = useAppDispatch();
  const formRef = useRef<ExplicitAny>(null);
  const methods = useForm<FormState>({
    values: useMemo(() => convertToPeriodSchema(periods), [periods]),
    resolver: zodResolver(schema),
  });
  useDirtyFormBlockNavigation(
    methods.formState,
    'OccasionalSubstitutePeriodForm',
    {
      // Allow the onSubmit method to do a observationDate change after
      // saving successfully. The actual form itself is disabled while the
      // form is in active edit state, so changing the observation date
      // through the search box is not possible while we are in edit mode
      // and "should" block navigation.
      allowSearchChange: true,
    },
  );

  const {
    control,
    handleSubmit,
    reset,
    formState: { isDirty },
    setValue,
  } = methods;

  const { append, fields, remove } = useFieldArray({
    control,
    name: 'periods',
  });

  const onSave = () => {
    submitFormByRef(formRef);
  };

  useEffect(() => {
    dispatch(setIsOccasionalSubstitutePeriodFormDirtyAction(isDirty));
  }, [dispatch, isDirty]);

  const onReset = () => {
    setIsResetting(false);
    reset();
  };

  const update = (index: number, flag: boolean) => {
    setValue(`periods.${index}.toBeDeleted`, !flag, {
      shouldDirty: true,
      shouldTouch: true,
    });
  };

  return (
    <div className="my-8">
      <FormProvider {...methods}>
        <form ref={formRef} onSubmit={handleSubmit(onSubmit)}>
          {fields.map((field, index) => (
            <OccasionalSubstitutePeriodRow
              field={field}
              index={index}
              key={field.id}
              remove={remove}
              update={update}
            />
          ))}
          <Row className="my-8 items-center gap-4">
            <Visible visible={!loading}>
              <TextAndIconButton
                className="group text-brand-darker"
                icon={
                  <AiOutlinePlus
                    className={twJoin(
                      'h-[32px] w-[32px] rounded-full p-[5px] text-xl',
                      'border border-brand bg-brand text-white',
                      'group-hover:bg-brand/50 group-active:bg-brand/50',
                    )}
                  />
                }
                text={t(($) => $.timetables.settings.addRow)}
                type="button"
                onClick={() => append(emptyRowObject)}
                testId={testIds.addRowButton}
              />
            </Visible>
          </Row>
          <Row className="my-8 justify-end gap-4">
            <SimpleButton
              onClick={() => setIsResetting(true)}
              disabled={!isDirty}
              id="cancel-button"
              testId={testIds.cancelButton}
              inverted
            >
              {t(($) => $.cancel)}
            </SimpleButton>

            <SimpleButton
              onClick={onSave}
              disabled={!isDirty}
              id="save-button"
              testId={testIds.saveButton}
            >
              {t(($) => $.save)}
            </SimpleButton>
          </Row>
        </form>
      </FormProvider>
      <ConfirmationDialog
        isOpen={isResetting}
        onCancel={() => setIsResetting(false)}
        onConfirm={onReset}
        title={t(($) => $.confirmResetOccasionalSubstitutePeriodDialog.title)}
        description={t(
          ($) => $.confirmResetOccasionalSubstitutePeriodDialog.description,
        )}
        confirmText={t(
          ($) => $.confirmResetOccasionalSubstitutePeriodDialog.confirmText,
        )}
        cancelText={t(
          ($) => $.confirmResetOccasionalSubstitutePeriodDialog.cancelText,
        )}
      />
    </div>
  );
};
